// Four presets end the same way: the design the user is about to be charged for appears over the
// field, with its price underneath. Points and polygons are the only difference, so they share the
// bbox mapping and — more importantly — the SAME self-check.
import type { Bbox } from "./geo";

export interface NormalizedDesign {
  /* * sample points, in 0..1 of the boundary bbox, y already flipped for screen space */
  points: { x: number; y: number }[];
  /* * zone outlines: one entry per ring, in the same 0..1 space */
  rings: {
    pts: { x: number; y: number }[];
    feature: number;
    zone: number | null;
  }[];
}

/* * Everything that made it onto the map, or null when too little did. */
export function normalizeDesign(
  fc: GeoJSON.FeatureCollection | null | undefined,
  bbox: Bbox | null | undefined,
): NormalizedDesign | null {
  if (!fc || !bbox) return null;
  const [x0, y0, x1, y1] = bbox;
  const w = x1 - x0 || 1;
  const h = y1 - y0 || 1;
  const at = (c: number[]) => ({
    x: (c[0]! - x0) / w,
    y: 1 - (c[1]! - y0) / h,
  });

  const points: { x: number; y: number }[] = [];
  const rings: {
    pts: { x: number; y: number }[];
    feature: number;
    zone: number | null;
  }[] = [];

  const features = (fc.features ?? []) as {
    properties?: Record<string, unknown>;
    geometry?: { type?: string; coordinates?: unknown };
  }[];
  features.forEach((f, fi) => {
    const g = f.geometry;
    if (!g) return;
    const zRaw = f.properties?.zone_number ?? f.properties?.zone;
    const zone =
      typeof zRaw === "number" && Number.isFinite(zRaw) ? zRaw : null;
    // Multi* is read too: an unexpected geometry should cost its own feature, never the overlay.
    switch (g.type) {
      case "Point":
        points.push(at(g.coordinates as number[]));
        break;
      case "MultiPoint":
        for (const c of (g.coordinates as number[][]) ?? []) points.push(at(c));
        break;
      case "Polygon":
        for (const ring of (g.coordinates as number[][][]) ?? [])
          rings.push({ pts: ring.map(at), feature: fi, zone });
        break;
      case "MultiPolygon":
        for (const poly of (g.coordinates as number[][][][]) ?? []) {
          for (const ring of poly ?? [])
            rings.push({ pts: ring.map(at), feature: fi, zone });
        }
        break;
      default:
        break;
    }
  });

  const all = [...points, ...rings.flatMap((r) => r.pts)];
  if (!all.length) return null;

  // Both geometries come off the same manifest so they should agree; if they ever don't, everything
  // lands outside the viewBox and draws as an EMPTY BOX, which is indistinguishable from "found none".
  const onMap = all.filter(
    (p) => p.x >= -0.05 && p.x <= 1.05 && p.y >= -0.05 && p.y <= 1.05,
  ).length;
  if (onMap / all.length < 0.6) {
    console.info(
      "[anim] %d/%d design vertices fell outside the boundary bbox — not drawing",
      all.length - onMap,
      all.length,
    );
    return null;
  }
  return { points, rings };
}

/* * An SVG path for one ring, scaled from 0..1 into a 100 × vh viewBox. */
export function ringPath(pts: { x: number; y: number }[], vh: number): string {
  if (!pts.length) return "";
  return (
    pts
      .map(
        (p, i) =>
          `${i === 0 ? "M" : "L"}${(p.x * 100).toFixed(2)} ${(p.y * vh).toFixed(2)}`,
      )
      .join(" ") + " Z"
  );
}
